import React, { Component, forwardRef, useEffect, useState } from 'react';

class StageBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError?.();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

// Server-rendered stand-in with the composition's exact aspect ratio, so the
// page never shifts when the player replaces it.
export const ShowcasePoster = ({ variant = 'reel' }) => (
  <div className={`stk-poster stk-poster--${variant}`} aria-hidden="true">
    <span className="stk-poster-mark" />
    <span className="stk-poster-window">
      <span className="stk-poster-bar" />
      <span className="stk-poster-line stk-poster-line--wide" />
      <span className="stk-poster-line" />
      <span className="stk-poster-chips">
        <span /><span /><span />
      </span>
    </span>
  </div>
);

// Poster first; once the Remotion runtime is loaded the player mounts on top
// and fades in. Any runtime error leaves the poster in place.
const ShowcaseStage = forwardRef(({
  runtime,
  compositionId,
  inputProps,
  initialFrame = 0,
  loop = false,
  onFrame,
  onPlayingChange,
  onEnded,
  onLive,
  variant = 'reel',
  className = ''
}, ref) => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // A player that threw stays on the poster even if its fade-in frame was
  // already queued.
  const live = ready && !failed;

  useEffect(() => {
    if (!runtime || failed) return undefined;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => setReady(true));
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [failed, runtime]);

  useEffect(() => {
    if (live) onLive?.();
  }, [live, onLive]);

  const Player = failed ? null : runtime?.ShowcasePlayer;

  return (
    <div className={`stk-stage stk-stage--${variant}${live ? ' is-live' : ''} ${className}`.trim()}>
      <ShowcasePoster variant={variant} />
      {Player ? (
        <StageBoundary onError={() => setFailed(true)}>
          <div className="stk-stage-player" aria-hidden="true">
            <Player
              ref={ref}
              compositionId={compositionId}
              inputProps={inputProps}
              initialFrame={initialFrame}
              loop={loop}
              onFrame={onFrame}
              onPlayingChange={onPlayingChange}
              onEnded={onEnded}
            />
          </div>
        </StageBoundary>
      ) : null}
    </div>
  );
});

ShowcaseStage.displayName = 'ShowcaseStage';

export default ShowcaseStage;
